import { useEffect, useRef, useState } from "react";
import { X, UploadCloud, Film, Image as ImageIcon, Plus, Trash2, Loader2, FileText, Play, Check, Sparkles, TriangleAlert, Pencil, Download, ShoppingCart, Copy } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { VIDEO_LANGS } from "@/lib/languages";
import { storeForCategory, buildItemUrl } from "@/lib/shop";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ThumbnailComposer } from "@/components/ThumbnailComposer";
import ThumbCropModal from "@/components/ThumbCropModal";

const CHUNK = 5 * 1024 * 1024; // 5MB
const MAX_BYTES = 30 * 1024 * 1024;

export function VideoUploadModal({ open, onClose, onDone, mode = "create", manual = null, topControls = null }) {
  const isEdit = mode === "edit";
  const isPortrait = !!manual && (manual.mode === "minute" || manual.orientation === "portrait" || manual.video_orientation === "portrait");
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [channels, setChannels] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [channel, setChannel] = useState("");
  const [addingChannel, setAddingChannel] = useState(false);
  const [subtopic, setSubtopic] = useState("");
  const [subtopicMap, setSubtopicMap] = useState({});
  const [language, setLanguage] = useState("en");
  const [sourceUrl, setSourceUrl] = useState("");
  const [description, setDescription] = useState("");
  const [describing, setDescribing] = useState(false);

  const generateDescription = async () => {
    if (!title.trim()) { toast.error("Add a title first, then Generate"); return; }
    setDescribing(true);
    try {
      const { data } = await api.post("/videos/describe", { title: title.trim(), category, subtopic });
      if (data?.description) { setDescription(data.description.slice(0, 2200)); toast.success("Description written ✨"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate — try again");
    } finally { setDescribing(false); }
  };
  const [price, setPrice] = useState("0");
  const [thumbnail, setThumbnail] = useState(null);
  const [file, setFile] = useState(null);
  const [products, setProducts] = useState([]);
  const [amazonText, setAmazonText] = useState("");
  const [amzImporting, setAmzImporting] = useState(false);

  const importAmazon = async () => {
    setAmzImporting(true);
    try {
      const { data } = await api.get(`/manuals/${manual.id}/amazon-links`);
      const links = data?.links || [];
      if (!links.length) { toast.error("No Amazon picks found for this topic"); return; }
      const current = amazonText.split("\n").map((l) => l.trim()).filter(Boolean);
      const have = new Set(current.flatMap((l) => (l.match(/https?:\/\/\S+/g) || [])));
      const fresh = links.filter((l) => !have.has(l.url)).map((l) => `${l.name} | ${l.url}`);
      if (!fresh.length) { toast.success("All links are already in the box"); return; }
      setAmazonText([...current, ...fresh].join("\n"));
      toast.success(`Imported ${fresh.length} Amazon link${fresh.length > 1 ? "s" : ""} — tap Save to keep them`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch Amazon links — try again");
    } finally { setAmzImporting(false); }
  };
  const [guidePdfName, setGuidePdfName] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [playUrl, setPlayUrl] = useState(null);
  const [playLoading, setPlayLoading] = useState(false);
  const [stepsImporting, setStepsImporting] = useState(false);
  const [showCatWarn, setShowCatWarn] = useState(false);
  const [showThumbStudio, setShowThumbStudio] = useState(false);
  const [tPrompt, setTPrompt] = useState("");
  const [tTitle, setTTitle] = useState("");
  const [tBusy, setTBusy] = useState(false);
  const [tThumbs, setTThumbs] = useState([]);
  const [showComposer, setShowComposer] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [uploadedThumbId, setUploadedThumbId] = useState(null);
  const [manualSteps, setManualSteps] = useState([]);
  const fileRef = useRef(null);

  const importStepsFromPdf = async () => {
    if (stepsImporting || !manual) return;
    setStepsImporting(true);
    try {
      const { data } = await api.post(`/manuals/${manual.id}/steps-from-pdf`);
      toast.success(`Imported ${data.count} steps from the PDF ✨`);
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import steps from the PDF");
    } finally {
      setStepsImporting(false);
    }
  };

  const playVideo = async () => {
    if (playLoading || !manual) return;
    if (playUrl) { setPlayUrl((u) => { if (u) URL.revokeObjectURL(u); return null; }); return; }
    setPlayLoading(true);
    try {
      const res = await api.get(`/manuals/${manual.id}/video/download`, { responseType: "blob" });
      setPlayUrl(URL.createObjectURL(res.data));
    } catch {
      toast.error("Couldn't load the video");
    } finally {
      setPlayLoading(false);
    }
  };

  useEffect(() => {
    api.get("/community/categories").then(({ data }) => setCats(data || [])).catch(() => {});
    api.get("/community/channels").then(({ data }) => setChannels(data || [])).catch(() => {});
    api.get("/paths/subtopics").then(({ data }) => setSubtopicMap(data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setPlayUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
    setCropSrc(null); setUploadedThumbId(null); setShowComposer(false);
    if (isEdit && manual) {
      setTitle(manual.title || "");
      setCategory(manual.share_category || manual.category || "");
      setChannel(manual.channel || "");
      setSubtopic(manual.subtopic || "");
      setLanguage(manual.language || "en");
      setSourceUrl(manual.source_url || "");
      setDescription(manual.description || "");
      setAddingChannel(false);
      setAmazonText("");
      setThumbnail(null);
      setGuidePdfName(manual.guide_pdf_name || "");
      // Pull the full record so we can prefill any existing Amazon links (https preserved).
      api.get(`/manuals/${manual.id}`).then(({ data }) => {
        setGuidePdfName(data?.guide_pdf_name || "");
        setPrice(String(Number(data?.bundle_price ?? manual.bundle_price ?? 0)));
        setManualSteps(Array.isArray(data?.steps) ? data.steps : []);
        const items = data?.shopping?.items || [];
        // Store + affiliate tag so name-only items become functional, tagged links (same as the PDF).
        const store = storeForCategory(user?.settings, data?.share_category || data?.category || "General").store || "Amazon";
        const tag = data?.amazon_affiliate_tag || user?.amazon_affiliate_tag || "";
        const lines = [];
        for (const it of items) {
          const url = it.url || "";
          const isPick = /^amazon pick$/i.test((it.product_name || "").trim());
          const name = isPick ? "" : (it.product_name || it.supply || "").replace(/^\s*\d+\s*[.)-]\s*/, "").trim();
          const price = it.price_range || "";
          const query = it.search_query || it.product_name || it.supply || name;
          // Re-attach an orphan link item to the preceding name-only line (undoes the old split).
          if (url && isPick && lines.length && !lines[lines.length - 1].url) {
            lines[lines.length - 1].url = url;
            if (price && !lines[lines.length - 1].price) lines[lines.length - 1].price = price;
          } else {
            lines.push({ name, url, price, query });
          }
        }
        // Build a tagged affiliate search link for any item that came back without a real URL,
        // so the edit field gets functional purchase links (with the affiliate ID) — not just names.
        for (const l of lines) {
          if (!l.url && (l.query || l.name)) l.url = buildItemUrl(store, l.query || l.name, tag);
        }
        setAmazonText(
          lines
            .filter((l) => l.url || l.name)
            .map((l) => [l.name, l.url, l.price].filter(Boolean).join(" | "))
            .join("\n")
        );
      }).catch(() => {});
    } else {
      setTitle(""); setCategory(""); setChannel(""); setSubtopic(""); setLanguage("en"); setSourceUrl(""); setDescription("");
      setAddingChannel(false); setAmazonText(""); setGuidePdfName("");
      setThumbnail(null); setFile(null); setProducts([]); setProgress(0); setPhase("");
    }
  }, [open, isEdit, manual]);

  if (!open) return null;

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please choose a video file (MP4)"); return; }
    if (f.size > MAX_BYTES) { toast.error("That video is over 30MB — please compress it first"); return; }
    setFile(f);
  };

  const onPickThumb = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    const r = new FileReader();
    r.onload = () => setCropSrc(r.result);
    r.readAsDataURL(f);
  };

  // After the user positions/zooms their uploaded image into a 16:9 frame, set it as the thumbnail
  // and store it server-side so it becomes a source photo for the FREE title creator (composer).
  const applyCroppedThumb = async (cropped) => {
    setCropSrc(null);
    setThumbnail(cropped);
    try {
      const { data } = await api.post("/thumbnails/upload", { image: cropped });
      if (data?.image_id) {
        setUploadedThumbId(data.image_id);
        setShowComposer(true);
        toast.success("Positioned! Add a title with the designer below — or just hit Save.");
        return;
      }
    } catch { /* fall through */ }
    toast.success("Thumbnail set — hit Save to keep it.");
  };

  const addProduct = () => setProducts((p) => [...p, { name: "", url: "", price: "" }]);
  const updProduct = (i, k, v) => setProducts((p) => p.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const delProduct = (i) => setProducts((p) => p.filter((_, j) => j !== i));

  const uploadGuidePdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file");
      return;
    }
    setPdfBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/manuals/${manual.id}/guide-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGuidePdfName(data.guide_pdf_name || f.name);
      toast.success("Guide PDF uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload the PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const removeGuidePdf = async () => {
    setPdfBusy(true);
    try {
      await api.delete(`/manuals/${manual.id}/guide-pdf`);
      setGuidePdfName("");
      toast.success("Guide PDF removed");
    } catch {
      toast.error("Couldn't remove the PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  // Parse the Amazon links text window into products. Accepts BOTH:
  //   • single line:  "Name | https://link | $price"
  //   • multi-line blocks (e.g. pasted from the PDF): a name line, then its link line, then price.
  // Only genuine Amazon affiliate links are kept; leading "1." numbering is stripped.
  const parseAmazonLinks = (text) => {
    const isUrl = (s) => /(https?:\/\/|www\.|amazon\.|amzn\.)/i.test(s);
    const isPrice = (s) => /\$\s?\d/.test(s) && s.length <= 16;
    const isAmazon = (u) => /(amazon\.|amzn\.)/i.test(u);
    const cleanName = (s) => s.replace(/^\s*\d+\s*[.)-]\s*/, "").trim();
    const products = [];
    let cur = null;
    const flush = () => { if (cur && cur.url) products.push(cur); cur = null; };
    for (const raw of (text || "").split("\n")) {
      const line = raw.trim();
      if (!line) continue;
      if (line.includes("|")) {
        flush();
        const parts = line.split("|").map((p) => p.trim()).filter(Boolean);
        let url = "", name = "", price = "";
        for (const p of parts) {
          if (!url && isUrl(p)) url = p;
          else if (!price && isPrice(p)) price = p;
          else name = name ? `${name} ${p}` : p;
        }
        if (url) products.push({ name: cleanName(name), url, price });
        continue;
      }
      if (isUrl(line)) {
        if (cur && !cur.url) cur.url = line;
        else { flush(); cur = { name: "", url: line, price: "" }; }
      } else if (isPrice(line)) {
        if (cur) cur.price = line;
      } else {
        flush();
        cur = { name: cleanName(line), url: "", price: "" };
      }
    }
    flush();
    return products.filter((p) => p.url && isAmazon(p.url));
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Give your video a title"); return; }
    if (!category) { setShowCatWarn(true); return; }
    if (isEdit) {
      setBusy(true);
      try {
        await api.post(`/manuals/${manual.id}/edit`, {
          title: title.trim(), category, channel, subtopic, language, source_url: sourceUrl, description,
          bundle_price: Math.max(0, parseFloat(price) || 0),
          products: parseAmazonLinks(amazonText),
          ...(thumbnail ? { thumbnail } : {}),
        });
        toast.success("Video updated");
        onDone?.();
        onClose();
      } catch {
        toast.error("Couldn't save changes");
      } finally { setBusy(false); }
      return;
    }
    if (!file) { toast.error("Choose a video to upload"); return; }
    setBusy(true);
    setProgress(0);
    setPhase("Uploading…");
    try {
      const uploadId = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()).replace(/[^a-z0-9-]/gi, "");
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("chunk", blob, "chunk");
        await api.post("/admin/videos/upload-chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      setPhase("Processing…");
      const clean = products.filter((p) => p.name.trim());
      const { data } = await api.post("/admin/videos/finalize", {
        upload_id: uploadId,
        title: title.trim(),
        category,
        channel,
        subtopic,
        language,
        source_url: sourceUrl,
        description,
        ...(thumbnail ? { thumbnail } : {}),
        products: clean,
      });
      toast.success("Video added to Your Manuals — post it to the Community when you're ready ✨");
      onDone?.(data.id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — try again");
    } finally {
      setBusy(false);
    }
  };

  const genThumbs = async () => {
    if (!tPrompt.trim()) { toast.error("Describe your thumbnail first"); return; }
    setTBusy(true); setTThumbs([]);
    try {
      const { data } = await api.post("/thumbnails", { prompt: tPrompt.trim(), title: tTitle.trim(), count: 1 });
      setTThumbs(data.thumbnails || []);
      if (!data.thumbnails?.length) toast.error("No thumbnails came back — try again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create thumbnails. Try again in a moment.");
    } finally {
      setTBusy(false);
    }
  };

  const applyGeneratedThumb = async (t) => {
    try {
      const resp = await fetch(imageUrl(t.image_id));
      const blob = await resp.blob();
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
      setThumbnail(dataUrl);
      setShowThumbStudio(false);
      toast.success("Thumbnail set — hit Save Video to keep it");
    } catch {
      toast.error("Couldn't use that thumbnail");
    }
  };

  const applyComposedThumb = async (image_id) => {
    try {
      const resp = await fetch(imageUrl(image_id));
      const blob = await resp.blob();
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
      setThumbnail(dataUrl);
      setShowComposer(false);
      setUploadedThumbId(null);
      toast.success("Thumbnail set — hit Save Video to keep it");
    } catch {
      toast.error("Couldn't use that thumbnail");
    }
  };

  const downloadCurrentThumb = async () => {
    try {
      const src = thumbnail || imageUrl(manual.hero_image_id);
      const resp = await fetch(src);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(title || manual?.title || "thumbnail").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 60) || "thumbnail"}-thumbnail.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Thumbnail downloading — ready for YouTube 🎬");
    } catch {
      toast.error("Couldn't download the thumbnail");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => !busy && onClose()} data-testid="video-upload-overlay">
      <div onClick={(e) => e.stopPropagation()} className={`sms-tweed vdark bg-[#0f0f16] border-2 border-[#5d8a52] text-white w-full max-w-lg rounded-3xl p-6 sms-fade-up max-h-[92vh] overflow-y-auto ${isEdit ? "edit-fields" : ""}`} style={{ boxShadow: "0 30px 90px -24px rgba(0,0,0,.85), 0 0 44px -14px rgba(93,138,82,.45)" }} data-testid="video-upload-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-extrabold flex items-center gap-2 text-white">
            <Film size={18} className="text-[#8fbf82]" /> {isEdit ? "Edit video" : "Upload video"}
          </h2>
          <button onClick={() => !busy && onClose()} className="text-white/50 hover:text-white transition-colors" data-testid="video-upload-close"><X size={18} /></button>
        </div>

        {isEdit && topControls && (
          <div className="sms-tweed mb-4 rounded-2xl border-2 border-[#2a3a26] bg-[#0a0a0f] p-2.5" data-testid="edit-controls-bar">
            {topControls}
          </div>
        )}

        {isEdit && (
          <button
            type="button"
            data-testid="edit-save-top-btn"
            onClick={submit}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 font-bold text-white bg-[#16a34a] hover:bg-[#15803d] py-3 rounded-2xl transition-colors disabled:opacity-70 mb-4"
          >
            {busy ? <><Loader2 size={17} className="spin" /> {phase || "Saving…"}</> : <><Check size={17} /> Save Video</>}
          </button>
        )}

        {isEdit && manual?.guide_pdf_id && (!manual?.steps || manual.steps.length === 0) && (
          <div className="mb-4" data-testid="import-steps-section">
            <button
              type="button"
              data-testid="import-steps-btn"
              onClick={importStepsFromPdf}
              disabled={stepsImporting}
              className="w-full flex items-center justify-center gap-2 font-bold text-[#5d8a52] bg-[#14241b] hover:bg-[#1b3322] py-3 rounded-2xl transition-colors disabled:opacity-70"
            >
              {stepsImporting ? <><Loader2 size={17} className="spin" /> Reading PDF…</> : <><FileText size={17} /> Import steps from PDF</>}
            </button>
            <p className="text-[11px] text-white/70 mt-1.5 text-center leading-snug">
              Pulls the numbered step titles from the attached guide PDF so they show on the video's page.
            </p>
          </div>
        )}

        {!isEdit && (
          <>
            <label
              data-testid="video-file-label"
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#3f5a38] rounded-2xl py-6 px-4 cursor-pointer hover:bg-[#0a0a0f] transition-colors text-center"
            >
              <UploadCloud size={26} className="text-[#5d8a52]" />
              <div className="text-sm font-bold text-white/70">{file ? file.name : "Choose an MP4 — 30 MB max"}</div>
              {file
                ? <div className={`text-xs ${file.size > 30 * 1024 * 1024 ? "text-[#e0455c] font-semibold" : "text-white/70"}`}>{(file.size / 1024 / 1024).toFixed(1)} MB{file.size > 30 * 1024 * 1024 ? " · over the 30MB limit — please compress it first" : ""}</div>
                : <div className="text-xs text-white/70">Tip: Vittyyo renders are ~10 MB and stream instantly. 30fps, no slow-mo.</div>}
              <input ref={fileRef} data-testid="video-file-input" type="file" accept="video/mp4,video/*" className="hidden" onChange={onPickFile} disabled={busy} />
            </label>
            {busy && (
              <div className="mt-3" data-testid="video-upload-progress">
                <div className="flex items-center justify-between text-xs font-semibold text-white/70 mb-1">
                  <span>{phase}</span><span>{progress}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#4f7746] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </>
        )}

        <label className="block text-xs font-bold text-white/70 mt-4 mb-1.5">Title</label>
        <input data-testid="video-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Homemade California Roll — the calm way" className="sms-input w-full px-4 py-2.5 text-sm" disabled={busy} />

        <div className="grid grid-cols-3 gap-2 mt-3">
          <div>
            <label className="block text-xs font-bold text-white/70 mb-1.5">Category</label>
            <select data-testid="video-category-select" value={category} onChange={(e) => { setCategory(e.target.value); setSubtopic(""); }} className="sms-input w-full px-3 py-2.5 text-sm cursor-pointer" disabled={busy}>
              <option value="" disabled>Select</option>
              {[...(cats.length ? cats : (category ? [category] : []))].sort((a, b) => a.localeCompare(b)).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-white/70 mb-1.5">Language</label>
            <select data-testid="video-language-select" value={language} onChange={(e) => setLanguage(e.target.value)} className="sms-input w-full px-3 py-2.5 text-sm cursor-pointer" disabled={busy}>
              {Object.entries(VIDEO_LANGS).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </div>
          <div>
            {(() => {
              const subs = subtopicMap[category] || [];
              const has = subs.length > 0;
              return (
                <>
                  <label className="block text-xs font-bold text-white/70 mb-1.5">
                    Sub-category {has && <span className="font-semibold text-[#8fbf82]">· powers the Path</span>}
                  </label>
                  <select
                    data-testid="video-subtopic-select"
                    value={subtopic}
                    onChange={(e) => setSubtopic(e.target.value)}
                    className="sms-input w-full px-3 py-2.5 text-sm cursor-pointer disabled:opacity-50"
                    disabled={busy || !has}
                    title={has ? `Pick a ${category} sub-category` : "No sub-categories for this category yet"}
                  >
                    <option value="">{has ? `Any ${category}` : (category ? "— none yet —" : "Pick a category first")}</option>
                    {subs.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </>
              );
            })()}
          </div>
        </div>

        <label className="block text-xs font-bold text-white/70 mt-3 mb-1.5">YouTube / source link</label>
        <input data-testid="video-source-input" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="Paste your YouTube / IG / TikTok link" className="sms-input w-full px-4 py-2.5 text-sm" disabled={busy} />

        <div className="mt-3 mb-1.5 flex items-center justify-between gap-2">
          <label className="text-xs font-bold text-white/70">
            Description <span className="font-semibold text-white/50">(optional — shows on your video's page)</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="tabular-nums text-white/60 text-xs">{description.length}/2200</span>
            <button
              type="button"
              data-testid="video-describe-copy-btn"
              onClick={() => navigator.clipboard.writeText(description).then(() => toast.success("Description copied — paste it on YouTube, IG, anywhere")).catch(() => toast.error("Couldn't copy — select the text and copy manually"))}
              disabled={!description.trim()}
              title="Copy description to clipboard"
              className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-white rounded-full px-3 py-1.5 border-2 border-[#5d8a52] bg-black transition-transform hover:scale-[1.04] disabled:opacity-40 disabled:hover:scale-100"
            >
              <Copy size={13} /> Copy
            </button>
            <button
              type="button"
              data-testid="video-describe-generate-btn"
              onClick={generateDescription}
              disabled={busy || describing || !title.trim()}
              title={title.trim() ? "AI-write a description for this topic" : "Add a title first"}
              className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-white rounded-full px-3 py-1.5 transition-transform hover:scale-[1.04] disabled:opacity-50 disabled:hover:scale-100"
              style={{ background: "#1e3aaa" }}
            >
              {describing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {describing ? "Writing…" : "Generate"}
            </button>
          </div>
        </div>
        <textarea data-testid="video-description-input" value={description} onChange={(e) => setDescription(e.target.value.slice(0, 2200))} maxLength={2200} rows={4} placeholder="In your own words, what's this video about? — or tap Generate." className="sms-input w-full px-4 py-2.5 text-sm resize-none" disabled={busy || describing} />

        {isEdit && (
          <>
            <label className="block text-xs font-bold text-white/70 mt-3 mb-1.5">Kit price <span className="font-semibold text-white/70">(what members pay for the full downloadable kit — pick Free or a price)</span></label>
            <div className="flex items-center gap-2.5">
              <select
                data-testid="video-price-select"
                value={(parseFloat(price) || 0) > 0 ? parseFloat(price).toFixed(2) : "0"}
                onChange={(e) => setPrice(e.target.value)}
                disabled={busy}
                className="sms-input w-[180px] px-3 py-2.5 text-sm bg-[#0f0f16] text-white"
              >
                {(() => {
                  const OPTS = ["0", "1.99", "2.99", "3.99", "4.99", "5.99", "6.99", "7.99"];
                  const cur = (parseFloat(price) || 0) > 0 ? parseFloat(price).toFixed(2) : "0";
                  const list = OPTS.includes(cur) ? OPTS : [...OPTS, cur];
                  return list.map((v) => (
                    <option key={v} value={v}>{(parseFloat(v) || 0) > 0 ? `$${parseFloat(v).toFixed(2)}` : "Free ($0.00)"}</option>
                  ));
                })()}
              </select>
              <span
                data-testid="video-price-preview"
                className="text-sm font-extrabold px-3 py-1.5 rounded-full"
                style={(parseFloat(price) || 0) > 0
                  ? { background: "#eef3ea", color: "#3f6238" }
                  : { background: "#e2f0ee", color: "#0f766e" }}
              >
                {(parseFloat(price) || 0) > 0 ? `$${(parseFloat(price) || 0).toFixed(2)}` : "Free"}
              </span>
            </div>
            <p className="text-[11px] text-white/45 mt-1">Price each video's kit on its own — a popular video can charge more, a new one can be free. You keep the earnings (minus platform fee) once your Stripe payouts are set up.</p>
          </>
        )}


        {isEdit && (
          <>
            <label className="block text-xs font-bold text-white/70 mt-3 mb-1.5">Thumbnail <span className="font-semibold text-white/70">(the image shown on your card)</span></label>
            <div className="flex flex-wrap items-center gap-2.5">
              {thumbnail || manual?.hero_image_id ? (
                <img
                  src={thumbnail || imageUrl(manual.hero_image_id)}
                  alt="Current thumbnail"
                  className="w-16 h-16 rounded-lg object-cover border-2 border-[#2a3a26] bg-[#14241b] shrink-0"
                  data-testid="edit-thumb-preview"
                />
              ) : (
                <div
                  data-testid="edit-thumb-placeholder"
                  className="w-16 h-16 rounded-lg border-2 border-dashed border-[#cbc4e6] bg-[#14241b] flex items-center justify-center text-[#a99fd4] shrink-0"
                >
                  <ImageIcon size={20} />
                </div>
              )}
              <button
                type="button"
                data-testid="edit-design-thumb-btn"
                onClick={() => setShowComposer((v) => !v)}
                disabled={busy}
                className="inline-flex items-center gap-2 text-sm font-bold text-white px-3.5 py-2 rounded-xl transition-colors"
                style={{ background: "#1e3aaa" }}
              >
                <Sparkles size={15} /> Design Your Thumbnail
              </button>
              <label data-testid="edit-thumb-label" className="inline-flex items-center gap-2 cursor-pointer text-sm font-bold text-white bg-[#1e3aaa] hover:bg-[#25429a] active:bg-[#1e3aaa] focus:bg-[#1e3aaa] focus:outline-none px-3.5 py-2 rounded-xl transition-colors">
                <UploadCloud size={15} /> {thumbnail ? "Choose a different image" : "Upload New Image"}
                <input data-testid="edit-thumb-input" type="file" accept="image/*" className="hidden" onChange={onPickThumb} disabled={busy} />
              </label>
              {(thumbnail || manual?.hero_image_id) && (
                <button
                  type="button"
                  data-testid="edit-download-thumb-btn"
                  onClick={downloadCurrentThumb}
                  disabled={busy}
                  className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#1e3aaa] hover:bg-[#25429a] active:bg-[#1e3aaa] focus:bg-[#1e3aaa] focus:outline-none px-3.5 py-2 rounded-xl transition-colors"
                >
                  <Download size={15} /> Download Current Thumbnail
                </button>
              )}
              {thumbnail && (
                <button type="button" data-testid="edit-thumb-reset" onClick={() => setThumbnail(null)} className="text-xs font-semibold text-white/70 hover:text-white/70">Undo</button>
              )}
            </div>
            <p className="text-xs text-white/70 mt-1.5">Upload your own, or tap <b>Design Your Thumbnail</b> to design one right here — font, color, layer &amp; position — no need to leave the editor.</p>

            {showThumbStudio && (
              <div className="sms-tweed mt-3 rounded-2xl border-2 border-[#5d8a52] bg-[#0a0a0f] p-3" data-testid="inline-thumb-studio">
                <div className="flex items-center gap-1.5 font-extrabold text-sm text-white mb-1">
                  <Sparkles size={15} className="text-[#5d8a52]" /> Thumbnail Studio
                </div>
                <textarea
                  data-testid="inline-thumb-prompt"
                  value={tPrompt}
                  onChange={(e) => setTPrompt(e.target.value)}
                  rows={2}
                  placeholder={'Describe what your thumbnail looks like — e.g. "Apple pie on a plate in a country kitchen"'}
                  className="sms-input w-full px-3 py-2 text-sm resize-none"
                  disabled={tBusy}
                />
                <p className="text-[11px] text-white/70 mt-1 leading-relaxed">
                  <span className="font-bold text-[#8fbf82]">Pro Tip:</span> add <span className="font-semibold">"Bokeh Background"</span> for a pro-looking blurry background.
                </p>
                <input
                  data-testid="inline-thumb-title"
                  value={tTitle}
                  onChange={(e) => setTTitle(e.target.value)}
                  maxLength={120}
                  placeholder="Title: How to make Apple Pie from Scratch"
                  className="sms-input w-full px-3 py-2 text-sm mt-1.5"
                  disabled={tBusy}
                />
                <button
                  type="button"
                  data-testid="inline-thumb-generate"
                  onClick={genThumbs}
                  disabled={tBusy}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 font-bold py-2.5 rounded-xl text-white text-sm transition-colors disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}
                >
                  {tBusy ? <><Loader2 size={15} className="spin" /> Designing your thumbnail…</> : <><Sparkles size={15} /> Create Your Thumbnail</>}
                </button>
                {tThumbs.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 mt-3" data-testid="inline-thumb-results">
                    {tThumbs.map((t, idx) => (
                      <div key={idx} className="rounded-xl overflow-hidden border-2 border-[#2a3a26] bg-[#0a0a0f]">
                        <img src={imageUrl(t.image_id)} alt={`Option ${idx + 1}`} className="w-full aspect-video object-cover" />
                        <button
                          type="button"
                          data-testid={`inline-thumb-use-${idx}`}
                          onClick={() => applyGeneratedThumb(t)}
                          className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-[#16a34a] hover:bg-[#15803d] py-2 transition-colors"
                        >
                          <Check size={13} /> Use this
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showComposer && (
              <div className="sms-tweed mt-3 rounded-2xl border-2 border-[#4f7746] bg-[#0a0a0f] p-3" data-testid="inline-thumb-composer">
                <div className="flex items-center gap-1.5 font-extrabold text-sm text-white mb-1">
                  <Pencil size={15} className="text-[#8fbf82]" /> Design Your Own — font, color, layer, position
                </div>
                <p className="text-[11px] text-white/70 mb-2 leading-relaxed">Pick one of your step photos and lay your title right on it — instant &amp; free (no credits).</p>
                <ThumbnailComposer
                  dark
                  portrait={isPortrait}
                  photos={[
                    ...(uploadedThumbId ? [{ id: uploadedThumbId }] : []),
                    ...manualSteps.filter((s) => s.thumbnail_id).map((s) => ({ id: s.thumbnail_id })),
                  ]}
                  defaultTitle={title}
                  onUse={applyComposedThumb}
                />
              </div>
            )}

            <div className="mt-4 pt-4 border-t-2 border-[#2a3a26]" data-testid="amazon-links-section">
              {isEdit && manual?.id && (
                <button type="button" data-testid="import-amazon-btn" onClick={importAmazon} disabled={busy || amzImporting}
                  className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 px-4 py-1.5 text-xs font-extrabold text-white transition-colors">
                  {amzImporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {amzImporting ? "Fetching links…" : "Import Amazon"}
                </button>
              )}
              <label className="flex items-center gap-1.5 text-sm font-extrabold text-white mb-1">
                <ShoppingCart size={15} className="text-[#8fbf82]" /> Amazon Links
              </label>
              <p className="text-xs text-white/70 mb-2 leading-relaxed">Paste each link on its own line. Your full <span className="font-semibold">https://</span> links (with your affiliate tag) are kept exactly as-is. Format: <span className="font-semibold">Name | link | $price</span> — name &amp; price optional.</p>
              <textarea
                data-testid="video-amazon-links"
                value={amazonText}
                onChange={(e) => setAmazonText(e.target.value)}
                rows={6}
                className="sms-input w-full px-4 py-2.5 text-sm font-mono resize-y"
                disabled={busy}
                spellCheck={false}
              />
            </div>

            <label className="block text-xs font-bold text-white/70 mt-4 mb-1.5">Full guide PDF <span className="font-semibold text-white/70">(auto-included in the kit — upload to replace)</span></label>
            {guidePdfName ? (
              <div data-testid="guide-pdf-current" className="flex items-center gap-2 rounded-xl border border-[#2a3a26] bg-[#0a0a0f] px-3 py-2.5">
                <FileText size={18} className="text-[#5d8a52] shrink-0" />
                <span className="text-sm text-white/70 font-semibold truncate flex-1">{guidePdfName}</span>
                <button type="button" data-testid="guide-pdf-remove" onClick={removeGuidePdf} disabled={pdfBusy}
                  className="text-[#dc2626] hover:text-[#b91c1c] shrink-0" title="Remove guide PDF">
                  {pdfBusy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
              </div>
            ) : (
              <label data-testid="guide-pdf-upload" className={`flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#d8cff5] bg-[#0a0a0f] px-3 py-3 text-sm font-bold text-[#5d8a52] ${pdfBusy ? "opacity-60" : "cursor-pointer hover:bg-[#14241b]"}`}>
                {pdfBusy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={18} />}
                {pdfBusy ? "Uploading…" : "Upload guide PDF"}
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={uploadGuidePdf} disabled={pdfBusy} />
              </label>
            )}
            <p className="text-xs text-white/70 mt-1.5">Good news — a polished step-by-step PDF is <span className="font-semibold text-[#5d8a52]">already built into your kit automatically</span>, so there's nothing to upload. Only upload your own if you'd rather use a custom PDF (it'll replace the auto one).</p>
          </>
        )}

        {!isEdit && (
          <>
            <label className="block text-xs font-bold text-white/70 mt-3 mb-1.5">Thumbnail <span className="font-semibold text-white/70">(optional — first frame auto-grabbed if blank)</span></label>
            <div className="flex items-center gap-3">
              <label data-testid="video-thumb-label" className="inline-flex items-center gap-2 cursor-pointer text-sm font-bold text-[#5d8a52] bg-[#14241b] hover:bg-[#1b3322] px-3.5 py-2 rounded-xl transition-colors">
                <ImageIcon size={15} /> {thumbnail ? "Change" : "Upload thumbnail"}
                <input data-testid="video-thumb-input" type="file" accept="image/*" className="hidden" onChange={onPickThumb} disabled={busy} />
              </label>
              {thumbnail && <img src={thumbnail} alt="thumb" className="w-12 h-12 rounded-lg object-cover border-2 border-[#3f5a38]" data-testid="video-thumb-preview" />}
            </div>

            {uploadedThumbId && (
              <div className="sms-tweed mt-3 rounded-2xl border-2 border-[#4f7746] bg-[#0a0a0f] p-3" data-testid="create-thumb-composer">
                <div className="flex items-center gap-1.5 font-extrabold text-sm text-white mb-1">
                  <Pencil size={15} className="text-[#8fbf82]" /> Add a title (optional) — font, color, layer, position
                </div>
                <p className="text-[11px] text-white/70 mb-2 leading-relaxed">Lay your title right on your uploaded image — instant &amp; free (no credits). Or skip and just upload.</p>
                <ThumbnailComposer
                  dark
                  photos={[{ id: uploadedThumbId }]}
                  defaultTitle={title}
                  onUse={applyComposedThumb}
                />
              </div>
            )}

            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold text-white/70">Amazon products <span className="font-semibold text-white/70">(optional)</span></label>
                <button data-testid="video-add-product" onClick={addProduct} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold text-[#5d8a52] hover:text-[#8fbf82]"><Plus size={13} /> Add</button>
              </div>
              <div className="space-y-2">
                {products.map((p, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`video-product-${i}`}>
                    <input value={p.name} onChange={(e) => updProduct(i, "name", e.target.value)} placeholder="Product name" className="sms-input flex-1 px-3 py-2 text-sm" disabled={busy} />
                    <input value={p.url} onChange={(e) => updProduct(i, "url", e.target.value)} placeholder="Amazon link" className="sms-input flex-1 px-3 py-2 text-sm" disabled={busy} />
                    <input value={p.price} onChange={(e) => updProduct(i, "price", e.target.value)} placeholder="$" className="sms-input w-16 px-2 py-2 text-sm" disabled={busy} />
                    <button onClick={() => delProduct(i)} disabled={busy} className="p-1.5 rounded-lg text-[#e0455c] hover:bg-[#241316]"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {!isEdit && (
          <button
            data-testid="video-upload-submit"
            onClick={submit}
            disabled={busy}
            className="w-full text-white font-bold py-3 rounded-2xl mt-5 flex items-center justify-center gap-2 disabled:opacity-70 transition-colors bg-[#16a34a] hover:bg-[#15803d]"
          >
            {busy ? <><Loader2 size={16} className="spin" /> {phase || "Saving…"}</> : "Upload to My Videos"}
          </button>
        )}
      </div>

      {cropSrc && (
        <ThumbCropModal image={cropSrc} onCancel={() => setCropSrc(null)} onConfirm={applyCroppedThumb} />
      )}

      {showCatWarn && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[#171426]/50 backdrop-blur-sm p-4"
          onClick={() => setShowCatWarn(false)}
          data-testid="cat-warn-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="sms-tweed bg-[#0a0a0f] rounded-2xl border-2 border-[#5d8a52] p-5 w-full max-w-xs text-center sms-fade-up">
            <div className="w-11 h-11 rounded-full bg-[#14241b] flex items-center justify-center mx-auto mb-2">
              <TriangleAlert size={22} className="text-[#5d8a52]" />
            </div>
            <p className="font-extrabold text-white">Must choose a category</p>
            <p className="text-sm text-white/70 mt-1">Pick a category so your video lands in the right place.</p>
            <button
              data-testid="cat-warn-ok"
              onClick={() => setShowCatWarn(false)}
              className="mt-4 w-full font-bold text-white bg-[#16a34a] hover:bg-[#15803d] py-2.5 rounded-xl transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
